/**
 * FeatureProvider - Context Provider für Feature-Toggles
 *
 * Verwaltet den Zustand aller Feature-Flags und ermöglicht
 * modulare Aktivierung/Deaktivierung von Features.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import {
  FEATURES,
  FEATURE_PRESETS,
  resolveFeatureDependencies
} from './FeatureFlags';

// Context
const FeatureContext = createContext(null);

// localStorage Key
const STORAGE_KEY = 'randalaise_features';

/**
 * Lädt persistente Features aus localStorage
 */
function loadPersistedFeatures() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to load persisted features:', e);
  }
  return {};
}

/**
 * Speichert persistente Features in localStorage
 */
function savePersistedFeatures(features) {
  try {
    // Nur persistente Features speichern
    const persistentFeatures = {};
    Object.entries(features).forEach(([id, enabled]) => {
      if (FEATURES[id]?.persistent) {
        persistentFeatures[id] = enabled;
      }
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistentFeatures));
  } catch (e) {
    console.warn('Failed to save persisted features:', e);
  }
}

/**
 * FeatureProvider Component
 */
export function FeatureProvider({ children, initialOverrides = {} }) {
  // State für Feature-Overrides
  const [featureOverrides, setFeatureOverrides] = useState(() => {
    const persisted = loadPersistedFeatures();
    return { ...persisted, ...initialOverrides };
  });

  // Persistente Features speichern bei Änderungen
  useEffect(() => {
    savePersistedFeatures(featureOverrides);
  }, [featureOverrides]);

  /**
   * Prüft ob ein Feature aktiviert ist
   */
  const isFeatureEnabled = useCallback((featureId) => {
    const feature = FEATURES[featureId];

    if (!feature) {
      console.warn(`Unknown feature: ${featureId}`);
      return false;
    }

    // Override hat Vorrang
    if (featureOverrides[featureId] !== undefined) {
      return featureOverrides[featureId];
    }

    // Prüfen ob abhängige Features deaktiviert sind
    if (feature.requires) {
      const allRequirementsMet = feature.requires.every(reqId => {
        const reqEnabled = featureOverrides[reqId] ?? FEATURES[reqId]?.defaultEnabled;
        return reqEnabled;
      });
      if (!allRequirementsMet) {
        return false;
      }
    }

    // Default-Wert
    return feature.defaultEnabled;
  }, [featureOverrides]);

  /**
   * Setzt ein Feature auf enabled/disabled
   */
  const setFeatureEnabled = useCallback((featureId, enabled) => {
    // Abhängigkeiten auflösen
    const changes = resolveFeatureDependencies(featureId, enabled);

    setFeatureOverrides(prev => ({
      ...prev,
      ...changes,
    }));
  }, []);

  /**
   * Setzt mehrere Features gleichzeitig
   */
  const setFeatures = useCallback((features) => {
    setFeatureOverrides(prev => ({
      ...prev,
      ...features,
    }));
  }, []);

  /**
   * Togglet ein Feature
   */
  const toggleFeature = useCallback((featureId) => {
    const currentValue = isFeatureEnabled(featureId);
    setFeatureEnabled(featureId, !currentValue);
  }, [isFeatureEnabled, setFeatureEnabled]);

  /**
   * Lädt ein Preset
   */
  const loadPreset = useCallback((presetId) => {
    const preset = FEATURE_PRESETS[presetId];
    if (preset) {
      setFeatureOverrides(prev => ({
        ...prev,
        ...preset.features,
      }));
    }
  }, []);

  /**
   * Aktiviert Test-Mode (Convenience-Funktion)
   */
  const enableTestMode = useCallback(() => {
    setFeatureEnabled('TEST_MODE', true);
  }, [setFeatureEnabled]);

  /**
   * Deaktiviert Test-Mode
   */
  const disableTestMode = useCallback(() => {
    setFeatureEnabled('TEST_MODE', false);
  }, [setFeatureEnabled]);

  /**
   * Setzt alle Overrides zurück
   */
  const resetFeatures = useCallback(() => {
    setFeatureOverrides({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Gibt alle aktiven Features zurück
   */
  const getActiveFeatures = useCallback(() => {
    return Object.keys(FEATURES).filter(id => isFeatureEnabled(id));
  }, [isFeatureEnabled]);

  // Context Value (memoized)
  const value = useMemo(() => ({
    // Abfragen
    isFeatureEnabled,
    getActiveFeatures,

    // Setzen
    setFeatureEnabled,
    setFeatures,
    toggleFeature,

    // Presets
    loadPreset,

    // Test-Mode Shortcuts
    enableTestMode,
    disableTestMode,
    isTestMode: isFeatureEnabled('TEST_MODE'),

    // Reset
    resetFeatures,

    // Alle Definitionen (für UI)
    features: FEATURES,
    presets: FEATURE_PRESETS,
    overrides: featureOverrides,
  }), [
    isFeatureEnabled,
    getActiveFeatures,
    setFeatureEnabled,
    setFeatures,
    toggleFeature,
    loadPreset,
    enableTestMode,
    disableTestMode,
    resetFeatures,
    featureOverrides,
  ]);

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

/**
 * Hook zum Zugriff auf Feature-Flags
 */
export function useFeatureFlags() {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureProvider');
  }
  return context;
}

/**
 * Hook für einzelnes Feature
 */
export function useFeature(featureId) {
  const { isFeatureEnabled } = useFeatureFlags();
  return isFeatureEnabled(featureId);
}

/**
 * Hook für Test-Mode Status
 */
export function useTestMode() {
  const { isTestMode, enableTestMode, disableTestMode } = useFeatureFlags();
  return { isTestMode, enableTestMode, disableTestMode };
}

/**
 * FeatureGate Component - Rendert Children nur wenn Feature aktiv
 */
export function FeatureGate({ feature, children, fallback = null }) {
  const isEnabled = useFeature(feature);

  if (!isEnabled) {
    return fallback;
  }

  return children;
}

/**
 * withFeature HOC - Wrapped Component nur wenn Feature aktiv
 */
export function withFeature(featureId, fallback = null) {
  return function withFeatureHOC(WrappedComponent) {
    return function FeatureWrappedComponent(props) {
      const isEnabled = useFeature(featureId);

      if (!isEnabled) {
        return fallback;
      }

      return <WrappedComponent {...props} />;
    };
  };
}

export default FeatureProvider;
